/**
 * @file
 * Form contact intent button behavior.
 *
 * @description
 * Intercepts clicks on anchors whose href contains
 * `?tp_contact_purpose=<value>` to prevent a full page reload. Instead it:
 *   1. Updates the browser URL via `history.pushState`.
 *   2. Checks the radio button whose ID is derived from the value
 *      (`edit-tp-contact-purpose-<value>`).
 *   3. Scrolls smoothly to the hash target (e.g. `#contact`) or falls back
 *      to the first webform on the page.
 *
 * On page load the current URL is also inspected so that direct visits
 * (e.g. shared links) still pre-select and scroll correctly.
 *
 * Usage in Twig — set the href on your button/link:
 *   href="?tp_contact_purpose=appointment#contact"
 *   href="?tp_contact_purpose=contact#contact"
 */

((Drupal, once) => {
  'use strict';

  const PARAM = 'tp_contact_purpose';

  /**
   * Derives the Drupal-generated radio button ID from a purpose value.
   * E.g. "appointment" → "edit-tp-contact-purpose-appointment".
   */
  const toRadioId = (purpose: string): string => `edit-${PARAM.replace(/_/g, '-')}-${purpose}`;

  /**
   * Checks the radio with the given id and fires change/input events so
   * Drupal States and any other listeners react correctly.
   */
  const activateRadio = (radioId: string): void => {
    const radio = document.getElementById(radioId) as HTMLInputElement | null;

    if (!radio || radio.type !== 'radio') {
      return;
    }

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    radio.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /**
   * Scrolls smoothly to the element with the given id.
   * Falls back to the first webform on the page when no element is found.
   * Moves keyboard focus to the target for screen-reader / keyboard users.
   */
  const scrollToElement = (elementId: string): void => {
    const target =
      document.getElementById(elementId) ??
      document.querySelector<HTMLElement>('.webform-submission-form, form[id^="webform"]');

    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }

    target.focus({ preventScroll: true });
  };

  /**
   * Reads `?tp_contact_purpose` + optional hash from the current page URL,
   * activates the matching radio, and scrolls to the target.
   */
  const applyFromUrl = (): void => {
    const params = new URLSearchParams(window.location.search);
    const purpose = params.get(PARAM);

    if (!purpose) {
      return;
    }

    activateRadio(toRadioId(purpose));

    const hash = window.location.hash.slice(1);
    if (hash) {
      scrollToElement(hash);
    }
  };

  /**
   * Contact intent CTA behavior.
   *
   * @description
   * Intercepts clicks on any anchor whose href contains
   * `tp_contact_purpose=` so the page does not reload. Also reads the same
   * parameter on page load to support direct links and back-navigation.
   */
  Drupal.behaviors.tpFormContactIntent = {
    attach(context: Document | HTMLElement) {
      // On the initial full-page load defer until after paint so the scroll
      // target is already in the layout. On AJAX re-attaches run synchronously.
      if (context === document) {
        window.requestAnimationFrame(() => applyFromUrl());
      } else {
        applyFromUrl();
      }

      const links = once('tp-contact-intent', `a[href*="${PARAM}="]`, context);

      links.forEach((el) => {
        const link = el as HTMLAnchorElement;

        link.addEventListener('click', (event: Event) => {
          event.preventDefault();

          const linkUrl = new URL(link.href, window.location.href);
          const purpose = linkUrl.searchParams.get(PARAM);

          if (!purpose) {
            return;
          }

          // Push the new URL (preserves current path, updates param + hash).
          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set(PARAM, purpose);
          nextUrl.hash = linkUrl.hash;
          history.pushState(null, '', nextUrl.toString());

          activateRadio(toRadioId(purpose));

          const hash = linkUrl.hash.slice(1);
          if (hash) {
            scrollToElement(hash);
          }
        });
      });
    },
  };
})(Drupal, once);
